import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Select } from '@/components/forms/Select';
import { TextInput } from '@/components/forms/TextInput';
import {
  SPECIALIZATION_LABELS_RU,
  SPECIALIZATIONS,
  type City,
  type Language,
  type Specialization,
} from '@pomoshnik/shared';

/**
 * Presenter Экрана 2.11.2 «Редактирование профиля» — Block 11 Phase 11.B (ADR-0091).
 *
 * Чистая функция props → JSX. State + side effects — в Container `ProfileEdit.tsx`.
 *
 * **Layout (P-046 shell):** `<main bg-canvas><Header/><section overflow-y-auto>
 * форма</section><BottomBar variant="default"/></main>`. **VideoButton НЕ дублируем**
 * — это дочерний экран ЛК (P-040 только на entry; хаб 2.11.1 видео уже имеет).
 *
 * **Форма (P-009 — только имя/специализация/город/телефон):**
 *   - Имя — `TextInput` (min 1).
 *   - Специализация — `Select` (SPECIALIZATIONS, лейблы `SPECIALIZATION_LABELS_RU`).
 *   - Город — `Select`, **только города своего региона** (фильтр в Container по
 *     `CITY_REGION_MAP[c] === region`, P-008).
 *   - Язык — дубль шапки (только показ, не редактируется тут).
 *   - Телефон — показ + «Изменить номер» (OTP-под-флоу, P-010) → модалка.
 *   - «Сохранить» (вертикально, P-019) с 5 состояниями P-021.
 *
 * **Design-system:** карточка/инпуты `bg-card` (P-038); body/headings `text-ink`
 * (P-039); кнопки вертикально (P-019); токены палитры (P-045); размеры (P-044).
 */

export type SaveState = 'idle' | 'loading' | 'success' | 'error';

const LANGUAGE_LABELS_RU: Record<Language, string> = {
  ru: 'Русский',
  kz: 'Қазақша',
  uz: "O'zbekcha",
};

export interface ProfileEditViewProps {
  name: string;
  specialization: Specialization | '';
  city: City | '';
  /** Города своего региона (отфильтрованы в Container по region, P-008). */
  cityOptions: ReadonlyArray<City>;
  /** Язык интерфейса (показ, не редактируется — дубль шапки). */
  lang: Language | null;
  /** Текущий телефон (показ). null — если профиль не загружен (P-009). */
  phone: string | null;
  saveState: SaveState;
  /** Inline-ошибки полей. */
  nameError?: string;
  specializationError?: string;
  /** Общая ошибка сохранения (server/network). */
  formError?: string;
  onNameChange: (v: string) => void;
  onSpecializationChange: (v: Specialization) => void;
  onCityChange: (v: City) => void;
  onChangePhone: () => void;
  onSave: () => void;
}

export function ProfileEditView(props: ProfileEditViewProps) {
  const specOptions = SPECIALIZATIONS.map((s) => ({
    value: s,
    label: SPECIALIZATION_LABELS_RU[s],
  }));
  const cityOpts = props.cityOptions.map((c) => ({ value: c, label: c }));
  const saving = props.saveState === 'loading';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Редактировать профиль" />

      <section className="flex-1 p-6 overflow-y-auto flex flex-col gap-4">
        <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-5">
          <TextInput
            name="name"
            label="Ваше имя"
            value={props.name}
            onChange={props.onNameChange}
            error={props.nameError}
            autoComplete="name"
            required
            maxLength={100}
            disabled={saving}
          />

          <Select
            name="specialization"
            label="Специализация"
            value={props.specialization}
            onChange={(v) => props.onSpecializationChange(v as Specialization)}
            options={specOptions}
            placeholder="Выберите специализацию"
            error={props.specializationError}
            required
            disabled={saving}
          />

          <Select
            name="city"
            label="Ваш город"
            value={props.city}
            onChange={(v) => props.onCityChange(v as City)}
            options={cityOpts}
            placeholder="Выберите город"
            hint="Доступны города вашего региона"
            required
            disabled={saving}
          />

          {/* Язык — дубль шапки (только показ, не редактируется тут). */}
          <div className="flex flex-col">
            <span className="text-ink font-semibold text-base mb-2">Язык интерфейса</span>
            <p className="text-muted text-[15px] m-0">
              {props.lang ? LANGUAGE_LABELS_RU[props.lang] : '—'} (меняется в верхнем меню)
            </p>
          </div>

          {/* Телефон — показ + «Изменить номер» (OTP-под-флоу, P-010). */}
          <div className="flex flex-col gap-2">
            <span className="text-ink font-semibold text-base">Телефон</span>
            <p className="text-ink text-[16px] m-0">{props.phone ? props.phone : '—'}</p>
            <button
              type="button"
              onClick={props.onChangePhone}
              aria-label="Изменить номер"
              className={[
                'w-full min-h-tap rounded-lg bg-transparent border-[1.5px] border-accent',
                'flex items-center justify-center gap-2 text-ink text-[16px] font-medium',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80',
              ].join(' ')}
            >
              Изменить номер
            </button>
          </div>
        </div>

        {props.formError !== undefined ? (
          <p role="alert" className="text-error text-sm">
            {props.formError}
          </p>
        ) : null}
        {props.saveState === 'success' ? (
          <p role="status" className="text-ink text-base font-medium">
            Профиль сохранён
          </p>
        ) : null}

        <button
          type="button"
          onClick={props.onSave}
          disabled={saving}
          aria-busy={saving ? true : undefined}
          className={[
            'w-full min-h-tap rounded-xl bg-cta border-b-2 border-cta-shadow',
            'text-ink text-[16px] font-bold px-4 py-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            'disabled:opacity-60 disabled:cursor-not-allowed',
          ].join(' ')}
        >
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </section>

      <BottomBar variant="default" />
    </main>
  );
}
